"use client";

import { type ReactNode, useState } from "react";
import {
  Notice,
  Panel,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY, WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  anovaView,
  benchmarkSentence,
  convertView,
  type EffectView,
  type FieldKey,
  type Fields,
  fromTView,
  independentView,
  pairedView,
} from "@/components/tools/stats-descriptive/effect-size-views";
import {
  Explanation,
  Formula,
  WorkingPanel,
} from "@/components/tools/stats-descriptive/ui";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { formatStat } from "@/lib/tool-kit/stats-descriptive/format";

/** English strings. A translated content file overrides these through `ui`. */
export const EFFECT_SIZE_CALCULATOR_STRINGS = {
  modeLabel: "What do you have?",
  modeIndependent: "Two independent groups",
  modePaired: "Paired or repeated measures",
  modeT: "A t statistic",
  modeAnova: "An ANOVA result",
  modeConvert: "An r, d or odds ratio to convert",
  group1: "Group 1 (treatment)",
  group2: "Group 2 (comparison)",
  condition1: "Condition 1 (e.g. before)",
  condition2: "Condition 2 (e.g. after)",
  meanLabel: "Mean",
  sdLabel: "Standard deviation",
  nLabel: "Sample size (n)",
  controlLabel: "Glass's Δ divides by the SD of",
  controlGroup1: "Group 1",
  controlGroup2: "Group 2",
  sdDiffLabel: "SD of the paired differences (optional, for d_z)",
  designLabel: "Design",
  designIndependent: "Independent groups",
  designPaired: "Paired samples",
  tLabel: "t statistic",
  tn1Label: "n in group 1",
  tn2Label: "n in group 2",
  tnLabel: "Number of pairs (n)",
  anovaSourceLabel: "From",
  anovaF: "F and its degrees of freedom",
  anovaSs: "Sums of squares",
  fLabel: "F",
  df1Label: "Effect df (numerator)",
  df2Label: "Error df (denominator)",
  ssEffectLabel: "SS for the effect",
  ssErrorLabel: "SS error (residual)",
  ssTotalLabel: "SS total",
  convertLabel: "Convert from",
  convertR: "Correlation r",
  convertD: "Cohen's d",
  convertOr: "Odds ratio",
  valueLabel: "Value",
  tryExample: "Try an example",
  clear: "Clear",
  exampleM1: "82.4",
  exampleSd1: "9.1",
  exampleN1: "30",
  exampleM2: "76.8",
  exampleSd2: "10.3",
  exampleN2: "28",
  examplePm1: "24.6",
  examplePsd1: "5.2",
  examplePm2: "21.3",
  examplePsd2: "4.8",
  exampleSdDiff: "3.9",
  exampleT: "2.45",
  exampleTn1: "30",
  exampleTn2: "28",
  exampleTn: "25",
  exampleF: "5.12",
  exampleDf1: "2",
  exampleDf2: "57",
  exampleSsEffect: "120",
  exampleSsError: "680",
  exampleSsTotal: "1050",
  exampleValue: "0.3",
  enterPrompt:
    "Fill in the fields above to see the effect size. Standard deviations must be positive and sample sizes at least 2.",
  results: "Results",
  cohensD: "Cohen's d",
  hedgesG: "Hedges' g",
  glassDelta: "Glass's Δ",
  pooledSd: "Pooled SD (sₚ)",
  meanDifference: "Mean difference",
  correction: "Correction factor J",
  dZ: "Cohen's d_z",
  dAv: "Cohen's d_av",
  averageSd: "Average SD",
  partialEta: "Partial η²",
  eta: "η²",
  cohensF: "Cohen's f",
  correlationR: "Correlation r",
  oddsRatio: "Odds ratio",
  workingTitle: "Formulas used",
  pooledLine:
    "sₚ = √(((n₁ − 1)s₁² + (n₂ − 1)s₂²) ÷ (n₁ + n₂ − 2)) = √(({n1m} × {v1} + {n2m} × {v2}) ÷ {df}) = {sp}",
  dLine: "d = (M₁ − M₂) ÷ sₚ = ({m1} − {m2}) ÷ {sp} = {d}",
  gLine:
    "g = J × d, with J = 1 − 3 ÷ (4(n₁ + n₂) − 9) = 1 − 3 ÷ (4 × {nn} − 9) = {j}, so g = {j} × {d} = {g}",
  glassLine: "Δ = (M₁ − M₂) ÷ s(comparison) = ({m1} − {m2}) ÷ {sd} = {delta}",
  dZLine: "d_z = mean difference ÷ SD of the differences = {diff} ÷ {sd} = {d}",
  dAvLine:
    "d_av = (M₁ − M₂) ÷ ((s₁ + s₂) ÷ 2) = ({m1} − {m2}) ÷ (({sd1} + {sd2}) ÷ 2) = {d}",
  dZMissing:
    "Enter the SD of the paired differences to get d_z, the version a paired t-test's power depends on.",
  tIndependentLine:
    "d = t × √(1 ÷ n₁ + 1 ÷ n₂) = {t} × √(1 ÷ {n1} + 1 ÷ {n2}) = {d}",
  tPairedLine: "d_z = t ÷ √n = {t} ÷ √{n} = {d}",
  partialFromFLine:
    "Partial η² = (F × df₁) ÷ (F × df₁ + df₂) = ({f} × {df1}) ÷ ({f} × {df1} + {df2}) = {eta}",
  fLine: "f = √(η² ÷ (1 − η²)) = √({eta} ÷ (1 − {eta})) = {f}",
  oneWayNote:
    "In a one-way ANOVA, η² equals partial η². In a factorial design η² needs the sums of squares — switch the source above.",
  etaLine: "η² = SS effect ÷ SS total = {effect} ÷ {total} = {eta}",
  partialLine:
    "Partial η² = SS effect ÷ (SS effect + SS error) = {effect} ÷ ({effect} + {error}) = {eta}",
  rToDLine: "d = 2r ÷ √(1 − r²) = 2 × {r} ÷ √(1 − {r}²) = {d}",
  dToRLine: "r = d ÷ √(d² + 4) = {d} ÷ √({d}² + 4) = {r}",
  orToDLine: "d = ln(OR) × √3 ÷ π = ln({or}) × √3 ÷ π = {d}",
  dToOrLine: "OR = exp(d × π ÷ √3) = exp({d} × π ÷ √3) = {or}",
  conversionNote:
    "The r–d formulas assume two groups of equal size. The odds-ratio conversion is the logit method of Chinn (2000), which assumes an underlying logistic distribution.",
  benchmarkLine:
    "By Cohen's (1988) benchmarks for {scale}, {value} counts as {label}.",
  negligible: "negligible — below small",
  small: "small",
  medium: "medium",
  large: "large",
  scaleD: "d (0.2 small, 0.5 medium, 0.8 large)",
  scaleR: "r (0.1 small, 0.3 medium, 0.5 large)",
  scaleEta: "η² (0.01 small, 0.06 medium, 0.14 large)",
  scaleF: "f (0.10 small, 0.25 medium, 0.40 large)",
  benchmarkCaveat:
    "Cohen meant these as a fallback for when nothing better is known. Compare your effect with others in your field and with what matters in practice.",
} as const;

type Strings = typeof EFFECT_SIZE_CALCULATOR_STRINGS;
type Mode = "independent" | "paired" | "t" | "anova" | "convert";
type Design = "independent" | "paired";
type AnovaSource = "f" | "ss";
type ConvertFrom = "r" | "d" | "or";

const EXAMPLES: Record<FieldKey, keyof Strings> = {
  m1: "exampleM1",
  sd1: "exampleSd1",
  n1: "exampleN1",
  m2: "exampleM2",
  sd2: "exampleSd2",
  n2: "exampleN2",
  pm1: "examplePm1",
  psd1: "examplePsd1",
  pm2: "examplePm2",
  psd2: "examplePsd2",
  sdDiff: "exampleSdDiff",
  t: "exampleT",
  tn1: "exampleTn1",
  tn2: "exampleTn2",
  tn: "exampleTn",
  f: "exampleF",
  df1: "exampleDf1",
  df2: "exampleDf2",
  ssEffect: "exampleSsEffect",
  ssError: "exampleSsError",
  ssTotal: "exampleSsTotal",
  value: "exampleValue",
};

const EMPTY = Object.fromEntries(
  Object.keys(EXAMPLES).map((key) => [key, ""])
) as Fields;

interface Choices {
  mode: Mode;
  control: 1 | 2;
  design: Design;
  source: AnovaSource;
  from: ConvertFrom;
}

function computeView(
  fields: Fields,
  choices: Choices,
  strings: Strings,
  num: (value: number) => string
): EffectView | null {
  switch (choices.mode) {
    case "independent":
      return independentView(fields, choices.control, strings, num);
    case "paired":
      return pairedView(fields, strings, num);
    case "t":
      return fromTView(fields, choices.design, strings, num);
    case "anova":
      return anovaView(fields, choices.source, strings, num);
    default:
      return convertView(fields, choices.from, strings, num);
  }
}

export default function EffectSizeCalculatorWidget() {
  const strings = useToolUi(EFFECT_SIZE_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [fields, setFields] = useState<Fields>(EMPTY);
  const [mode, setMode] = useState<Mode>("independent");
  const [control, setControl] = useState<"1" | "2">("2");
  const [design, setDesign] = useState<Design>("independent");
  const [source, setSource] = useState<AnovaSource>("f");
  const [from, setFrom] = useState<ConvertFrom>("r");

  const num = (value: number) => formatStat(fmt, value);
  const view = computeView(
    fields,
    { mode, control: control === "1" ? 1 : 2, design, source, from },
    strings,
    num
  );
  const benchmark = view
    ? benchmarkSentence(view.headline, strings, num)
    : null;

  const input = (key: FieldKey, label: string): ReactNode => (
    <TextField
      id={`es-${key}`}
      label={label}
      numeric
      onChange={(value) =>
        setFields((previous) => ({ ...previous, [key]: value }))
      }
      value={fields[key]}
    />
  );

  function fillExample() {
    const example = { ...EMPTY };
    for (const key of Object.keys(EXAMPLES) as FieldKey[]) {
      example[key] = strings[EXAMPLES[key]];
    }
    setFields(example);
  }

  const forms: Record<Mode, ReactNode> = {
    independent: (
      <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Panel title={strings.group1}>
            {input("m1", strings.meanLabel)}
            {input("sd1", strings.sdLabel)}
            {input("n1", strings.nLabel)}
          </Panel>
          <Panel title={strings.group2}>
            {input("m2", strings.meanLabel)}
            {input("sd2", strings.sdLabel)}
            {input("n2", strings.nLabel)}
          </Panel>
        </div>
        <PillGroup
          label={strings.controlLabel}
          name="es-control"
          onChange={setControl}
          options={[
            { value: "1", label: strings.controlGroup1 },
            { value: "2", label: strings.controlGroup2 },
          ]}
          value={control}
        />
      </div>
    ),
    paired: (
      <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Panel title={strings.condition1}>
            {input("pm1", strings.meanLabel)}
            {input("psd1", strings.sdLabel)}
          </Panel>
          <Panel title={strings.condition2}>
            {input("pm2", strings.meanLabel)}
            {input("psd2", strings.sdLabel)}
          </Panel>
        </div>
        <div className="max-w-sm">{input("sdDiff", strings.sdDiffLabel)}</div>
      </div>
    ),
    t: (
      <div className="space-y-4">
        <PillGroup
          label={strings.designLabel}
          name="es-design"
          onChange={setDesign}
          options={[
            { value: "independent", label: strings.designIndependent },
            { value: "paired", label: strings.designPaired },
          ]}
          value={design}
        />
        <div className="grid gap-4 sm:grid-cols-3">
          {input("t", strings.tLabel)}
          {design === "independent" ? input("tn1", strings.tn1Label) : null}
          {design === "independent" ? input("tn2", strings.tn2Label) : null}
          {design === "paired" ? input("tn", strings.tnLabel) : null}
        </div>
      </div>
    ),
    anova: (
      <div className="space-y-4">
        <PillGroup
          label={strings.anovaSourceLabel}
          name="es-anova"
          onChange={setSource}
          options={[
            { value: "f", label: strings.anovaF },
            { value: "ss", label: strings.anovaSs },
          ]}
          value={source}
        />
        <div className="grid gap-4 sm:grid-cols-3">
          {source === "f" ? input("f", strings.fLabel) : null}
          {source === "f" ? input("df1", strings.df1Label) : null}
          {source === "f" ? input("df2", strings.df2Label) : null}
          {source === "ss" ? input("ssEffect", strings.ssEffectLabel) : null}
          {source === "ss" ? input("ssError", strings.ssErrorLabel) : null}
          {source === "ss" ? input("ssTotal", strings.ssTotalLabel) : null}
        </div>
      </div>
    ),
    convert: (
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          id="es-from"
          label={strings.convertLabel}
          onChange={setFrom}
          options={[
            { value: "r", label: strings.convertR },
            { value: "d", label: strings.convertD },
            { value: "or", label: strings.convertOr },
          ]}
          value={from}
        />
        {input("value", strings.valueLabel)}
      </div>
    ),
  };

  return (
    <>
      <section className="space-y-4">
        <PillGroup
          label={strings.modeLabel}
          name="es-mode"
          onChange={setMode}
          options={[
            { value: "independent", label: strings.modeIndependent },
            { value: "paired", label: strings.modePaired },
            { value: "t", label: strings.modeT },
            { value: "anova", label: strings.modeAnova },
            { value: "convert", label: strings.modeConvert },
          ]}
          value={mode}
        />
        {forms[mode]}
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_SECONDARY}
            onClick={fillExample}
            type="button"
          >
            {strings.tryExample}
          </button>
          <button
            className={BUTTON_SECONDARY}
            onClick={() => setFields(EMPTY)}
            type="button"
          >
            {strings.clear}
          </button>
        </div>
      </section>

      {view ? (
        <section className="space-y-4">
          <h2 className={WIDGET_H2}>{strings.results}</h2>
          <div aria-live="polite" className="space-y-4">
            <StatGrid>
              {view.cards.map((card) => (
                <StatCard
                  emphasis={card.emphasis}
                  key={card.label}
                  label={card.label}
                  value={card.value}
                />
              ))}
            </StatGrid>
            {benchmark ? <Explanation>{benchmark}</Explanation> : null}
          </div>
          <WorkingPanel title={strings.workingTitle}>
            {view.working.map((line) => (
              <Formula key={line}>{line}</Formula>
            ))}
            <Explanation>{strings.benchmarkCaveat}</Explanation>
          </WorkingPanel>
        </section>
      ) : (
        <Notice>{strings.enterPrompt}</Notice>
      )}
    </>
  );
}
